<template>
  <v-card id="acceptance-identity" class="pa-4 mb-4">
    <div class="text-subtitle-1 font-weight-medium mb-3">
      {{ $t("acceptance.identity.title") }}
    </div>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="`${$t('acceptance.identity.vin_read')} *`"
          :model-value="values['identity.vinRead']"
          :readonly="readonly || frozen"
          :bg-color="bg('identity.vinRead')"
          maxlength="40"
          hide-details
          @update:model-value="change('identity.vinRead', $event)"
        />
        <div class="d-flex flex-wrap align-center text-caption mt-1">
          <span v-if="normalisedVin" class="text-grey-darken-1 mr-2">
            {{ $t("acceptance.identity.normalised") }}: {{ normalisedVin }}
          </span>
          <v-chip v-if="vinDiffers" size="x-small" color="amber-darken-3">
            {{ $t("acceptance.identity.differs_from_vehicle") }}
          </v-chip>
        </div>
        <correction-note
          group="identity.vin"
          part="action"
          :frozen="frozen"
          :corrections="corrections"
          :can-correct="canCorrect"
          :disabled="correctDisabled"
          @correct="$emit('correct', $event)"
          @open-log="$emit('open-log', $event)"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="$t('acceptance.identity.plate')"
          :model-value="values['identity.plate']"
          :readonly="readonly || frozen"
          :bg-color="bg('identity.plate')"
          maxlength="20"
          hide-details
          @update:model-value="change('identity.plate', $event)"
        />
        <v-chip
          v-if="plateDiffers"
          size="x-small"
          color="amber-darken-3"
          class="mt-1"
        >
          {{ $t("acceptance.identity.differs_from_vehicle") }}
        </v-chip>
        <correction-note
          group="identity.plate"
          part="action"
          :frozen="frozen"
          :corrections="corrections"
          :can-correct="canCorrect"
          :disabled="correctDisabled"
          @correct="$emit('correct', $event)"
          @open-log="$emit('open-log', $event)"
        />
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-autocomplete
          variant="outlined"
          density="comfortable"
          :clearable="!readonly && !frozen"
          :label="$t('acceptance.identity.country')"
          :items="countryItems"
          :model-value="values['identity.plateCountry']"
          :readonly="readonly || frozen"
          :bg-color="bg('identity.plateCountry')"
          hide-details
          @update:model-value="change('identity.plateCountry', $event)"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <div class="text-caption text-grey-darken-1">
          {{ $t("acceptance.identity.class") }} *
        </div>
        <v-btn-toggle
          :model-value="values['identity.vehicleClass']"
          :disabled="readonly || frozen"
          :class="{
            'acceptance-unsaved': isHighlighted('identity.vehicleClass'),
          }"
          variant="outlined"
          density="comfortable"
          color="primary"
          divided
          @update:model-value="change('identity.vehicleClass', $event || null)"
        >
          <v-btn value="M1">M1</v-btn>
          <v-btn value="N1">N1</v-btn>
        </v-btn-toggle>
        <correction-note
          group="identity.vehicleClass"
          part="action"
          :frozen="frozen"
          :corrections="corrections"
          :can-correct="canCorrect"
          :disabled="correctDisabled"
          @correct="$emit('correct', $event)"
          @open-log="$emit('open-log', $event)"
        />
      </v-col>
    </v-row>
    <correction-note
      group="identity.vin"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />
    <correction-note
      group="identity.plate"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />
    <correction-note
      group="identity.vehicleClass"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />
  </v-card>
</template>

<script>
import { normaliseVin } from "@/components/Acceptance/draftForm.mjs";
import COUNTRY_CODES from "@/components/Acceptance/countries";
import CorrectionNote from "@/components/Acceptance/correctionNote.vue";

function normalisePlate(value) {
  return normaliseVin(value); // same rule as the API's normalisePlate
}

export default {
  name: "AcceptanceIdentityCard",
  components: { "correction-note": CorrectionNote },
  props: {
    values: { type: Object, required: true },
    car: { type: Object, default: null },
    readonly: { type: Boolean, default: false },
    highlight: { type: Array, default: () => [] },
    // Confirmed: some fields are frozen and change through a correction (corrections.mjs).
    frozen: { type: Boolean, default: false },
    corrections: { type: Object, default: () => ({}) },
    canCorrect: { type: Boolean, default: false },
    correctDisabled: { type: Boolean, default: false },
  },
  emits: ["change", "correct", "open-log"],
  computed: {
    normalisedVin() {
      return normaliseVin(this.values["identity.vinRead"]);
    },
    // Live versions of the API's mismatch flags (shown, never blocking).
    vinDiffers() {
      const carVin = this.car ? normaliseVin(this.car.vin) : null;
      return Boolean(
        this.normalisedVin && carVin && this.normalisedVin !== carVin,
      );
    },
    plateDiffers() {
      const plate = normalisePlate(this.values["identity.plate"]);
      const carPlate = this.car ? normalisePlate(this.car.targa) : null;
      return Boolean(plate && carPlate && plate !== carPlate);
    },
    countryItems() {
      let names = null;
      try {
        names = new Intl.DisplayNames([this.$i18n.locale], { type: "region" });
      } catch (err) {
        names = null;
      }
      const items = COUNTRY_CODES.map((value) => ({
        value,
        title: `${value} · ${(names && names.of(value)) || value}`,
      }));
      const italy = items.filter((item) => item.value === "IT");
      const others = items
        .filter((item) => item.value !== "IT")
        .sort((a, b) => a.title.localeCompare(b.title));
      return [...italy, ...others];
    },
  },
  methods: {
    isHighlighted(path) {
      return this.highlight.includes(path);
    },
    bg(path) {
      return this.isHighlighted(path) ? "amber-lighten-5" : undefined;
    },
    change(path, value) {
      this.$emit("change", path, value);
    },
  },
};
</script>

<style scoped>
.acceptance-unsaved {
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 0 0 2px #ffe082;
}
</style>
